import { isForeignEmbed } from './portal'

// The part of this frame that is on the browser's screen, in the frame's own
// viewport coordinates - for overlays rendered inside a foreign embed
// (RTM-10728).
//
// A cross-origin host hands the frame nothing about its scroll position, and
// `position: fixed` inside the frame pins to the frame box, which on a
// foreign host is as tall as the host made it - a sheet at `bottom: 0` lands
// at the bottom of the box, far below the screen. IntersectionObserver is the
// one API that still crosses the boundary: for a cross-origin frame it
// reports the target's intersection with the top-level viewport
// (intersectionRect, frame coordinates) while withholding rootBounds, the
// contract ad viewability relies on.
//
// It reports ratio changes only, and a single target spanning the frame keeps
// a constant ratio while the screen moves through the middle of it - one
// screen of a 20000px frame is one screen wherever it is. So the targets are
// a ladder of fixed, inert strips down the frame: the host's scroll moves
// strips on and off the screen, each crossing fires, and the visible slice is
// the union of the visible strips, pixel-exact at both ends because a partly
// visible strip reports its exact visible part. Thresholds at quarters of a
// strip give an update every STEP_PX of host scroll.
//
// One ladder per frame, built on first use and kept alive: the slice is then
// already known the instant a sheet opens, instead of one frame later.
const STEP_PX = 16
const STRIP_PX = 4 * STEP_PX
const MAX_STRIPS = 800
const THRESHOLDS = [ 0, 0.25, 0.5, 0.75, 1 ]

let slice = null
let observer = null
let strips = []
let ladderHeight = 0
const visibleByStrip = new Map()
const listeners = new Set()

const stripHeightFor = (frameHeight) => Math.max(STRIP_PX, Math.ceil(frameHeight / MAX_STRIPS))

const createStrip = (top, height) => {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  el.style.cssText =
    `position:fixed;left:0;right:0;top:${top}px;height:${height}px;pointer-events:none;visibility:hidden;`
  document.body.appendChild(el)
  return el
}

const mergeVisible = () => {
  let top = Infinity
  let bottom = -Infinity
  visibleByStrip.forEach((rect) => {
    top = Math.min(top, rect.top)
    bottom = Math.max(bottom, rect.bottom)
  })
  // A frame scrolled fully off screen has no visible strip; the last known
  // slice is a better answer than a zero-height sheet.
  if (bottom > top) {
    slice = { top, bottom, height: bottom - top }
  }
}

const onEntries = (entries) => {
  entries.forEach((entry) => {
    const rect = entry.intersectionRect
    if (rect.width > 0 && rect.height > 0) {
      visibleByStrip.set(entry.target, { top: rect.top, bottom: rect.bottom })
    } else {
      visibleByStrip.delete(entry.target)
    }
  })
  mergeVisible()
  listeners.forEach((listener) => listener(slice))
}

// (Re)builds the ladder for the current frame height.
const buildLadder = () => {
  if (observer) observer.disconnect()
  strips.forEach((strip) => strip.remove())
  visibleByStrip.clear()

  ladderHeight = window.innerHeight
  const stripHeight = stripHeightFor(ladderHeight)
  strips = []
  for (let top = 0; top < ladderHeight; top += stripHeight) {
    strips.push(createStrip(top, Math.min(stripHeight, ladderHeight - top)))
  }

  observer = new IntersectionObserver(onEntries, { threshold: THRESHOLDS })
  strips.forEach((strip) => observer.observe(strip))
}

export const trackVisibleSlice = () => {
  if (observer || !isForeignEmbed() || typeof IntersectionObserver === 'undefined') return
  buildLadder()
  window.addEventListener('resize', () => {
    if (window.innerHeight !== ladderHeight) buildLadder()
  })
}

// The last slice seen, or null before the first observation (or on hosts
// where the frame is our own and fixed positioning already works).
export const getVisibleSlice = () => slice

// Runs listener with the slice after each observation; returns the unsubscribe.
export const subscribeVisibleSlice = (listener) => {
  trackVisibleSlice()
  listeners.add(listener)
  return () => listeners.delete(listener)
}
